// Panel de proyectos, como el de Framer (ficha divflow-spec 01): barra lateral con workspace, buscador, Domains y
// Projects; cabecera con orden y New project; tarjetas de 260 px.
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Archive, ChevronDown, Globe, LayoutGrid, Mail, Plus, Search } from 'lucide-react';
import { api, type Site } from '../api.ts';
import { t } from '../i18n/index.ts';
import { navigate } from '../router.ts';
import { errorText, useApp } from '../store.ts';
import { ago, fullDate } from '../format.ts';
import { LogoMark } from '../ui/Logo.tsx';
import { Menu, MenuButton, useMenuState, type MenuEntry } from '../ui/Menu.tsx';
import { Dialog } from '../ui/Dialog.tsx';

type Sort = 'viewed' | 'edited' | 'alpha';
const SORT_KEY = 'df:sort';
const viewedKey = (id: string) => `df:viewed:${id}`;

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function markViewed(id: string): void {
  try {
    localStorage.setItem(viewedKey(id), String(Date.now()));
  } catch {
    // sin almacenamiento: no pasa nada
  }
}

export function useWorkspaceMenu(): MenuEntry[] {
  const me = useApp((s) => s.me);
  const setMe = useApp((s) => s.setMe);
  return [
    { label: t('ws.settings'), shortcut: 'Shift+,', onSelect: () => navigate('/settings/account') },
    { label: t('ws.invite'), onSelect: () => navigate('/settings/members'), disabled: me?.role !== 'owner' },
    { label: t('ws.status'), onSelect: () => navigate('/settings/status') },
    { separator: true },
    { label: t('ws.dayMode'), shortcut: 'Alt+N', disabled: true, title: t('common.comingSoon') },
    { separator: true },
    {
      label: t('ws.signOut'),
      onSelect: async () => {
        await api.logout().catch(() => {});
        setMe(null);
        navigate('/login', true);
      },
    },
  ];
}

function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const config = useApp((s) => s.config);
  const putSite = useApp((s) => s.putSite);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const slug = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { site } = await api.createSite(name);
      putSite(site);
      onClose();
      navigate(`/sites/${site.id}/versions`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={t('projects.new')} onClose={onClose}>
      <form className="col" onSubmit={submit}>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('projects.namePlaceholder')} autoFocus required maxLength={80} />
        <p className="hint ellipsis" title={`${slug || 'site'}.${config?.baseDomain ?? ''}`}>
          {t('projects.subdomainHint', { host: `${slug || 'site'}.${config?.baseDomain ?? ''}` })}
        </p>
        {error ? <p className="error">{error}</p> : null}
        <div className="dialog-foot">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
            {t('common.create')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function RenameDialog({ site, onClose }: { site: Site; onClose: () => void }) {
  const putSite = useApp((s) => s.putSite);
  const notify = useApp((s) => s.notify);
  const [name, setName] = useState(site.name);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      putSite((await api.updateSite(site.id, { name })).site);
      onClose();
    } catch (err) {
      notify(errorText(err), true);
    }
  };
  return (
    <Dialog title={t('projects.rename')} onClose={onClose}>
      <form className="col" onSubmit={submit}>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus required maxLength={80} />
        <div className="dialog-foot">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn primary">
            {t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function statusBadge(site: Site) {
  if (site.archived) return <span className="badge">{t('projects.archived')}</span>;
  if (!site.published) return <span className="badge">{t('projects.draft')}</span>;
  if (site.uptime.state === 'down') return <span className="badge red">{t('projects.down')}</span>;
  return <span className="badge blue">{t('projects.live')}</span>;
}

function ProjectCard({ site, sort, onRename }: { site: Site; sort: Sort; onRename: (s: Site) => void }) {
  const putSite = useApp((s) => s.putSite);
  const notify = useApp((s) => s.notify);
  const menu = useMenuState();
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
  const pointRef = useRef<HTMLSpanElement>(null);
  const viewed = Number(readLocal(viewedKey(site.id)) ?? 0) || null;
  const sub = sort === 'viewed' && viewed ? t('projects.viewed', { when: ago(viewed) }) : t('projects.edited', { when: ago(site.updatedAt) });
  const open = () => {
    markViewed(site.id);
    navigate(`/sites/${site.id}`);
  };
  const items: MenuEntry[] = [
    { label: t('projects.openNewTab'), onSelect: () => window.open(`/sites/${site.id}`, '_blank', 'noopener') },
    {
      label: t('projects.copyLink'),
      onSelect: () => {
        void navigator.clipboard.writeText(site.hosts.url).then(() => notify(t('projects.linkCopied')));
      },
    },
    { label: t('projects.visit'), disabled: !site.published, onSelect: () => window.open(site.hosts.url, '_blank', 'noopener') },
    { separator: true },
    { label: t('projects.rename'), onSelect: () => onRename(site) },
    { separator: true },
    {
      label: site.archived ? t('projects.unarchive') : t('projects.archive'),
      onSelect: async () => {
        try {
          putSite((await api.updateSite(site.id, { archived: !site.archived })).site);
        } catch (err) {
          notify(errorText(err), true);
        }
      },
    },
  ];
  return (
    <div
      className="card"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => e.key === 'Enter' && open()}
      onContextMenu={(e) => {
        e.preventDefault();
        setPoint({ x: e.clientX, y: e.clientY });
        menu.toggle();
      }}
    >
      <div className="thumb">
        <span className="initial">{site.name.slice(0, 1).toUpperCase()}</span>
      </div>
      <div className="card-meta">
        <div className="col grow" style={{ gap: 5 }}>
          <span className="name ellipsis" title={site.name}>
            {site.name}
          </span>
          <span className="sub ellipsis" title={fullDate(sort === 'viewed' && viewed ? viewed : site.updatedAt)}>
            {sub}
          </span>
        </div>
        {statusBadge(site)}
        <span ref={pointRef} style={{ position: 'fixed', left: point?.x ?? 0, top: point?.y ?? 0, width: 0, height: 0 }} />
        <Menu anchor={point ? pointRef : menu.anchor} open={menu.open} onClose={() => { menu.close(); setPoint(null); }} items={items} />
      </div>
    </div>
  );
}

export function Dashboard({ filter }: { filter: 'all' | 'archive' }) {
  const me = useApp((s) => s.me);
  const sites = useApp((s) => s.sites);
  const loaded = useApp((s) => s.sitesLoaded);
  const loadSites = useApp((s) => s.loadSites);
  const notify = useApp((s) => s.notify);
  const wsMenu = useWorkspaceMenu();
  const ws = useMenuState();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>(() => (readLocal(SORT_KEY) as Sort) || 'viewed');
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<Site | null>(null);

  useEffect(() => {
    loadSites().catch((e) => notify(errorText(e), true));
  }, [loadSites, notify]);

  const chooseSort = (s: Sort) => {
    setSort(s);
    try {
      localStorage.setItem(SORT_KEY, s);
    } catch {
      // nada
    }
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = sites.filter(
      (s) =>
        (filter === 'archive' ? s.archived : !s.archived) &&
        (!q || s.name.toLowerCase().includes(q) || s.slug.includes(q) || s.domains.some((d) => d.hostname.includes(q))),
    );
    const viewed = (s: Site) => Number(readLocal(viewedKey(s.id)) ?? 0);
    return [...list].sort((a, b) =>
      sort === 'alpha' ? a.name.localeCompare(b.name) : sort === 'edited' ? b.updatedAt - a.updatedAt : viewed(b) - viewed(a) || b.updatedAt - a.updatedAt,
    );
  }, [sites, filter, query, sort]);

  const domains = useMemo(
    () => sites.filter((s) => !s.archived).flatMap((s) => s.domains.filter((d) => d.status === 'active' && d.primary).map((d) => ({ host: d.hostname, site: s }))),
    [sites],
  );

  const sortLabels: Record<Sort, string> = { viewed: t('projects.sortViewed'), edited: t('projects.sortEdited'), alpha: t('projects.sortAlpha') };

  return (
    <div className="dash">
      <aside className="sidebar">
        <button ref={ws.anchor} type="button" className="ws-button" onClick={ws.toggle} aria-haspopup="menu" aria-expanded={ws.open}>
          <span className="logo">
            <LogoMark height={18} />
          </span>
          <span className="ellipsis">{t('ws.name')}</span>
          <ChevronDown size={10} className="chev" />
        </button>
        <Menu anchor={ws.anchor} open={ws.open} onClose={ws.close} items={wsMenu} width={240} />
        <div className="search" style={{ marginTop: 18 }}>
          <Search size={12} />
          <input className="input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('common.search')} />
        </div>
        <div className="sep" style={{ marginTop: 9 }} />
        <div className="scroll">
          <div className="side-title">{t('projects.domains')}</div>
          {domains.length === 0 ? <div className="side-item dim" style={{ cursor: 'default' }}>{t('projects.noDomains')}</div> : null}
          {domains.map((d) => (
            <button key={d.host} type="button" className="side-item" onClick={() => navigate(`/sites/${d.site.id}/domains`)} title={d.host}>
              <Globe size={12} />
              <span className="ellipsis">{d.host}</span>
              <span className={`dot ${d.site.uptime.state === 'down' ? 'down' : d.site.uptime.state === 'up' ? 'up' : ''}`} style={{ marginLeft: 'auto' }} />
            </button>
          ))}
          <div className="sep" style={{ marginTop: 10 }} />
          <div className="side-title">{t('projects.projects')}</div>
          <button type="button" className={`side-item${filter === 'all' ? ' active' : ''}`} onClick={() => navigate('/')}>
            <LayoutGrid size={12} />
            <span>{t('projects.all')}</span>
          </button>
          <button type="button" className={`side-item${filter === 'archive' ? ' active' : ''}`} onClick={() => navigate('/archive')}>
            <Archive size={12} />
            <span>{t('projects.archiveTitle')}</span>
          </button>
          <button type="button" className="side-item" disabled title={t('common.comingSoon')} style={{ opacity: 0.5, cursor: 'default' }}>
            <Plus size={12} />
            <span>{t('projects.newFolder')}</span>
          </button>
        </div>
        <div className="side-foot">
          <button type="button" className="side-item" style={{ width: 'auto' }} disabled={me?.role !== 'owner'} onClick={() => navigate('/settings/members')}>
            <Mail size={12} />
            <span className="ellipsis">{t('projects.inviteTeam')}</span>
          </button>
          <button
            type="button"
            className="btn field"
            onClick={() => void navigator.clipboard.writeText(location.origin).then(() => notify(t('projects.linkCopied')))}
          >
            {t('projects.copyLink')}
          </button>
        </div>
      </aside>
      <main className="main">
        <div className="main-inner">
          <div className="main-head">
            <h1 className="ellipsis">{filter === 'archive' ? t('projects.archiveTitle') : t('projects.all')}</h1>
            <div className="row">
              <MenuButton
                className="btn field"
                align="end"
                items={(['viewed', 'edited', 'alpha'] as Sort[]).map((s) => ({ label: `${sort === s ? '✓ ' : ''}${sortLabels[s]}`, onSelect: () => chooseSort(s) }))}
              >
                <span>{sortLabels[sort]}</span>
                <ChevronDown size={10} color="#777777" />
              </MenuButton>
              <button type="button" className="btn primary" onClick={() => setCreating(true)}>
                {t('projects.new')}
              </button>
            </div>
          </div>
          {!loaded ? null : visible.length === 0 ? (
            <div className="empty">
              <span>{query ? t('projects.noResults') : filter === 'archive' ? t('projects.emptyArchive') : t('projects.empty')}</span>
              {filter === 'all' && !query ? (
                <button type="button" className="btn primary" onClick={() => setCreating(true)}>
                  {t('projects.new')}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="grid">
              {visible.map((s) => (
                <ProjectCard key={s.id} site={s} sort={sort} onRename={setRenaming} />
              ))}
            </div>
          )}
        </div>
      </main>
      {creating ? <NewProjectDialog onClose={() => setCreating(false)} /> : null}
      {renaming ? <RenameDialog site={renaming} onClose={() => setRenaming(null)} /> : null}
    </div>
  );
}
